import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import ProgressBar from "@/components/base/ProgressBar";
import { courses as mockCourses } from "@/mocks/vivelo";
import {
  fetchLessons,
  fetchCompletedLessonIds,
  fetchLessonVideoUrl,
  markLessonProgress,
  type Lesson,
} from "@/services/courseProgress";
import { useCourseProgress } from "@/hooks/useCourseProgress";

export default function CursoDetalle() {
  const { courseId = "" } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { refresh: refreshCourseProgress } = useCourseProgress();

  const course = useMemo(() => mockCourses.find((c) => c.id === courseId), [courseId]);

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [marking, setMarking] = useState(false);

  // Carga el catálogo de lecciones del curso y cuáles ya completó el usuario.
  useEffect(() => {
    if (!courseId) return;
    let cancelled = false;
    setLoadingLessons(true);
    Promise.all([fetchLessons(courseId), fetchCompletedLessonIds(courseId)]).then(([ls, completed]) => {
      if (cancelled) return;
      setLessons(ls);
      setCompletedIds(completed);
      // Selecciona la primera lección no completada, o la primera del curso.
      const firstPending = ls.find((l) => !completed.has(l.id));
      setSelectedLessonId((firstPending ?? ls[0])?.id ?? null);
      setLoadingLessons(false);
    });
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  // Pide la URL firmada del video cada vez que cambia la lección seleccionada.
  useEffect(() => {
    if (!selectedLessonId) {
      setVideoUrl(null);
      return;
    }
    let cancelled = false;
    setVideoLoading(true);
    setVideoUrl(null);
    fetchLessonVideoUrl(selectedLessonId).then((video) => {
      if (cancelled) return;
      setVideoUrl(video?.url ?? null);
      setVideoLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedLessonId]);

  const selectedLesson = lessons.find((l) => l.id === selectedLessonId) ?? null;
  const completedCount = lessons.filter((l) => completedIds.has(l.id)).length;
  const coursePercent = lessons.length ? Math.round((completedCount / lessons.length) * 100) : 0;

  const handleMarkComplete = useCallback(async () => {
    if (!selectedLessonId || !courseId || marking) return;
    setMarking(true);
    const ok = await markLessonProgress(courseId, selectedLessonId, { completed: true });
    setMarking(false);
    if (!ok) return;

    setCompletedIds((prev) => new Set(prev).add(selectedLessonId));
    refreshCourseProgress();

    // Avanza automáticamente a la siguiente lección pendiente, si hay una.
    const idx = lessons.findIndex((l) => l.id === selectedLessonId);
    const next = lessons.slice(idx + 1).find((l) => !completedIds.has(l.id));
    if (next) setSelectedLessonId(next.id);
  }, [selectedLessonId, courseId, marking, lessons, completedIds, refreshCourseProgress]);

  if (!course) {
    return (
      <AppShell headerVariant="onLight" showBack>
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 text-center">
          <p className="text-foreground-600">No encontramos este curso.</p>
          <button
            onClick={() => navigate("/app/cursos")}
            className="mt-4 inline-flex items-center gap-2 bg-primary-500 text-background-950 rounded-full px-5 py-2.5 font-heading font-bold text-sm hover:bg-primary-600 cursor-pointer"
          >
            Volver a Cursos
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell headerVariant="onLight" showBack>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Encabezado del curso */}
        <div className="flex items-start gap-4">
          <span className="w-14 h-14 flex-shrink-0 rounded-2xl bg-primary-500/15 text-primary-600 flex items-center justify-center">
            <i className={`${course.icon} text-2xl`}></i>
          </span>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">{course.title}</h1>
            <p className="text-foreground-600 text-sm mt-1">{course.description}</p>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <span>Progreso del curso</span>
            <span className="font-semibold text-foreground-800">
              {coursePercent}% · {completedCount} de {lessons.length} lecciones
            </span>
          </div>
          <div className="mt-1"><ProgressBar value={coursePercent} color="primary" /></div>
        </div>

        {/* Reproductor */}
        <div className="mt-6 rounded-2xl overflow-hidden bg-black aspect-video flex items-center justify-center">
          {videoLoading ? (
            <div className="text-white/60 text-sm flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i> Cargando video...
            </div>
          ) : videoUrl ? (
            // key fuerza a remontar el <video> cuando cambia la URL firmada.
            <video key={videoUrl} controls autoPlay src={videoUrl} className="w-full h-full" />
          ) : (
            <div className="text-white/70 text-sm text-center px-6">
              <i className="ri-film-line text-3xl block mb-2"></i>
              Esta lección todavía se está preparando.
              <br />Vuelve a intentarlo más tarde.
            </div>
          )}
        </div>

        {selectedLesson && (
          <div className="mt-4 flex items-center justify-between gap-3">
            <div>
              <div className="font-heading font-bold text-foreground-950">{selectedLesson.title}</div>
              <div className="text-xs text-foreground-500">Lección {selectedLesson.order} de {lessons.length}</div>
            </div>
            {completedIds.has(selectedLesson.id) ? (
              <span className="inline-flex items-center gap-1 text-xs bg-primary-500/15 text-primary-700 px-3 py-1.5 rounded-full font-semibold whitespace-nowrap">
                Completada <i className="ri-check-line"></i>
              </span>
            ) : (
              <button
                onClick={handleMarkComplete}
                disabled={marking}
                className="inline-flex items-center gap-2 bg-primary-500 text-background-950 rounded-full px-4 py-2 text-sm font-heading font-bold hover:bg-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {marking ? "Guardando..." : "Marcar como completada"} <i className="ri-check-line"></i>
              </button>
            )}
          </div>
        )}

        {/* Lista de lecciones */}
        <div className="mt-8">
          <h2 className="font-heading font-bold text-foreground-950 mb-3">Lecciones</h2>
          {loadingLessons ? (
            <p className="text-sm text-foreground-500">Cargando lecciones...</p>
          ) : (
            <ol className="space-y-2">
              {lessons.map((l) => {
                const isSelected = l.id === selectedLessonId;
                const isDone = completedIds.has(l.id);
                return (
                  <li key={l.id}>
                    <button
                      onClick={() => setSelectedLessonId(l.id)}
                      className={`w-full flex items-center gap-3 rounded-xl border p-3.5 text-left cursor-pointer transition-colors ${
                        isSelected
                          ? "border-primary-500 bg-primary-500/5"
                          : "border-background-200 bg-background-100 hover:border-primary-300"
                      }`}
                    >
                      <span
                        className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${
                          isDone ? "bg-primary-500 text-background-950" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {isDone ? <i className="ri-check-line"></i> : l.order}
                      </span>
                      <span className="flex-1 min-w-0 text-sm font-semibold text-foreground-900">{l.title}</span>
                      {isSelected && <i className="ri-play-circle-fill text-primary-600 text-xl"></i>}
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <button
          onClick={() => navigate("/app/cursos")}
          className="mt-8 inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-900 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i> Volver a Cursos
        </button>
      </div>
    </AppShell>
  );
}
